const DEBUGGING: boolean = process.env.NODE_ENV === 'development'

import * as telegram from './telegram'
import * as utils from './utils'
import { BaseObject, MongoObject } from './_baseClasses'
import { roleType, Feedback } from './Feedback'

const AUTO_EXTEND_WINDOW_SEC: number = 60

export enum auctionStatus {
  Open = 1, Ended = 3, Canceled = 4
}

export enum auctionType {
  Regular = 1, Flash = 2
}

export enum paymentType {
  Paypal = 1, Venmo = 4, Zelle = 7, Crypto = 10, Other = 99
}

export class Auction extends MongoObject {
  protected static collectionName: string = 'auctions'           // IMPORTANT

  public static MAX_SELLER_NOTE: number = 255

  public static MIN_COLLECTOR_SCORE_TO_BID: number = 500      // went from 250 to 500 when we went from CS to TSS
  public static MIN_COLLECTOR_SCORE_TO_POST: number = 2000    // went from 1000 to 2000 when we went from CS to TSS

  public static LAUNCH_DATE_ISO_STRING: string = '2021-07-30T23:00:00.000Z'   // launch on July 30, 2021 at 4pm PST

  public static MIN_HRS_BEFORE_CANCELABLE: number = 24
  public static MIN_HRS_BEFORE_LEAVING_UNFULFILLED_FEEDBACK: number = 24

  public type: auctionType = null
  public momentID: number = null        // numeric momentID used by flow
  public setID: number = null           // for fast UI filtering
  public playID: number = null          // for fast UI filtering
  public subeditionID: number = null
  public serialNumber: number = null
  public name: string = null            // for fast UI filtering

  public seller: string = null          // flowAddress of the seller, no prefix
  public highestBidder: string = null   // flowAddress of the highest bidder, no prefix
  public sellerUserId: string = null          // userId of the seller,
  public highestBidderUserId: string = null   // userId of the highest bidder

  public startDate: Date = null
  public startingPrice: number = null
  public reservePrice: number = null
  public durationInSec: number = null
  public endDate: Date = null                   // original endDate, not including extended time
  public endDateBasedOnLatestBid: Date = null   // for shouldAutoExtend auctions, set to be AUTO_EXTEND_WINDOW_SEC after the latest bid
  public status: auctionStatus = null
  public shouldAutoExtend: boolean = null
  public didAutoExendOnLastBid: boolean = null
  public noteFromSeller: string = null
  public paymentTypes: paymentType[] = null

  public processedForDealsAndAlerts: boolean = null
  public insertedIntoDeals: boolean = null

  public endedSuccessfully: boolean = null
  public whenFullfilled: Date = null
  public whenSentUnfulfilledNotice: Date = null

  // bids are in a separate collection, but this is for speed
  public currentPrice: number = null
  public numBids: number = null

  // complex, class-based fields (require to be hydrated after loading from external sources like mongo or elastic)

  // client-only field not meant to be stored in db - utils_mongo.insertOne() and utils_mongo.insertMany() are instrumented to do this
  public _temp: { feedbacks?: Feedback[] } = {}

  public constructor(po?: object) {
    super()
    if (po) this.injectPlainObject(po)
    this.hydrate()
  }

  protected hydrate(): void {
    if (typeof this.startDate === 'string') this.startDate = new Date(this.startDate)
    if (typeof this.endDate === 'string') this.endDate = new Date(this.endDate)
    if (typeof this.endDateBasedOnLatestBid === 'string') this.endDateBasedOnLatestBid = new Date(this.endDateBasedOnLatestBid)
    if (this._temp && this._temp.feedbacks) this._temp.feedbacks = this._temp.feedbacks.map((f) => new Feedback(f))
    super.hydrate()
  }

  public isSafe(): boolean {
    return true
  }

  public static getBidIncrement(currentPrice: number) {
    if (currentPrice <= 10) return 0.10
    if (currentPrice <= 25) return 0.25
    if (currentPrice <= 100) return 1
    if (currentPrice <= 250) return 2.50
    if (currentPrice <= 500) return 5
    if (currentPrice <= 1000) return 10
    if (currentPrice <= 2000) return 20
    if (currentPrice <= 5000) return 50
    return 100
  }

  public getCurrentEndDate() {
    if (this.endDateBasedOnLatestBid && this.endDateBasedOnLatestBid > this.endDate) return this.endDateBasedOnLatestBid
    return this.endDate
  }

  public getLiveTokenAuctionLink(): string {
    return `/listings/auctions?auctionId=${this.getIdAsString()}`
  }

  public isSuccessful(): boolean {
    return (this.numBids > 0 && this.highestBidder && this.currentPrice >= this.reservePrice)
  }

  public static getPaymentTypeString(type: paymentType): string {
    switch (type) {
      case paymentType.Paypal: return 'PayPal'
      case paymentType.Venmo: return 'Venmo'
      case paymentType.Zelle: return 'Zelle'
      case paymentType.Crypto: return 'Crypto'
      case paymentType.Other: return 'Other'
    }
    return '???'
  }

  public isCancelable(): boolean {
    const nowTimeMS: number = new Date().getTime()
    if (this.status === auctionStatus.Canceled) return false
    if (this.status === auctionStatus.Open) return this.numBids === 0         // if open, then can cancel only if no bids
    if (this.status === auctionStatus.Ended) {
      if (this.whenFullfilled) return false                                   // already fulfilled
      if (!this.endedSuccessfully) return false                               // no need to cancel
      if (nowTimeMS - this.getCurrentEndDate().getTime() < Auction.MIN_HRS_BEFORE_CANCELABLE * 3600 * 1000) return false     // too soon to cancel
      return true                                                             // can cancel if say, user didn't pay
    }
  }

  public isFeedbackLeavable(authorRole: roleType): boolean {
    const nowTimeMS: number = new Date().getTime()
    if (!this.endedSuccessfully) return false                // can't leave feedback if no one actually won
    if (this.status === auctionStatus.Open) return false     // can't leave feedback if not even done
    if (this.status === auctionStatus.Ended && this.whenFullfilled) return true        // most common case for a good outcome

    // bad cases 

    if (authorRole === roleType.Seller) {
      if (this.status === auctionStatus.Canceled) return true  // seller can leave feedback on a non-fullfilled auction only after they cancel it (or of course fullfill it)
      return false
    }

    if (authorRole === roleType.Buyer) {
      if (this.status === auctionStatus.Canceled) return true  // seller canceled and buyer is mad?
      if (this.status === auctionStatus.Ended) {
        if (nowTimeMS - this.getCurrentEndDate().getTime() < Auction.MIN_HRS_BEFORE_LEAVING_UNFULFILLED_FEEDBACK * 3600 * 1000) return false     // too soon to leave feedback
        return true  // case where perhaps seller didn't send the moment and didn't cancel it either (AWOL seller)

      }
    }
  }

  public alreadyHasFeedback(role: roleType): boolean {
    if (!this._temp.feedbacks) return false
    for (const f of this._temp.feedbacks)  if (f.authorRole === role) return true
    return false
  }

  public static getAuctionListingsLink(): string {
    return `/listings/auctions`
  }

}


(async () => {
})();
